import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

const MATCH_PARAM_TYPES = ['username', 'unionid']

const accessStatus = ref('idle')
const matchedCrowdCodesByType = {
  username: ref(new Set()),
  unionid: ref(new Set()),
}
const queriedCrowdCodesByType = {
  username: new Set(),
  unionid: new Set(),
}
const activeCrowdRequestsByType = {
  username: null,
  unionid: null,
}

let activeToken = ''
let cachedUserInfo = null
let activeUserInfoRequest = null
let requestSequence = 0

const getDefaultMatchParamType = (webPageType) =>
  Number(webPageType) === 4 ? 'unionid' : 'username'

const getMatchParamType = (matchParamType, webPageType) => {
  if (MATCH_PARAM_TYPES.includes(matchParamType)) return matchParamType
  return getDefaultMatchParamType(webPageType)
}

const getQueryString = (value) => {
  const queryValue = Array.isArray(value) ? value[0] : value
  return typeof queryValue === 'string' ? queryValue : ''
}

const normalizeCrowdCodes = (crowdCodes) =>
  Array.from(
    new Set(
      (Array.isArray(crowdCodes) ? crowdCodes : [])
        .map((crowdCode) => String(crowdCode).trim())
        .filter(Boolean),
    ),
  )

const collectCrowdCodesByType = (dslContent, webPageType) => {
  const crowdCodesByType = {
    username: new Set(),
    unionid: new Set(),
  }
  const normalizedDslContent = Array.isArray(dslContent) ? dslContent : []

  normalizedDslContent.forEach((item) => {
    if (item?.type !== 'maskComponent') return
    const matchParamType = getMatchParamType(
      item.crowdMatchParamType,
      webPageType,
    )
    normalizeCrowdCodes(item.visibleCrowdPackageIds).forEach((crowdCode) => {
      crowdCodesByType[matchParamType].add(crowdCode)
    })
  })

  return crowdCodesByType
}

const resetAccessState = (token = '', status = 'idle') => {
  requestSequence += 1
  activeToken = token
  cachedUserInfo = null
  activeUserInfoRequest = null
  MATCH_PARAM_TYPES.forEach((matchParamType) => {
    matchedCrowdCodesByType[matchParamType].value = new Set()
    queriedCrowdCodesByType[matchParamType] = new Set()
    activeCrowdRequestsByType[matchParamType] = null
  })
  accessStatus.value = status
}

const useCrowdPackageAccess = () => {
  const route = useRoute()
  const store = useStore()

  const getUserInfo = async (token, currentSequence) => {
    if (cachedUserInfo) return cachedUserInfo
    if (activeUserInfoRequest) return activeUserInfoRequest

    activeUserInfoRequest = (async () => {
      const userResponse = await store.dispatch('getMaskUserInfo', { token })
      if (currentSequence !== requestSequence) return null
      const userInfo = userResponse?.data
      if (!userInfo || typeof userInfo !== 'object') {
        throw new Error('用户信息响应异常')
      }
      cachedUserInfo = userInfo
      return cachedUserInfo
    })()

    try {
      return await activeUserInfoRequest
    } finally {
      if (currentSequence === requestSequence) activeUserInfoRequest = null
    }
  }

  const queryCrowdCodes = async (
    matchParamType,
    requestedCrowdCodes,
    identityValue,
    currentSequence,
  ) => {
    if (activeCrowdRequestsByType[matchParamType]) {
      await activeCrowdRequestsByType[matchParamType]
      if (currentSequence !== requestSequence) return false
    }

    const missingCrowdCodes = requestedCrowdCodes.filter(
      (crowdCode) =>
        !queriedCrowdCodesByType[matchParamType].has(crowdCode),
    )
    if (!missingCrowdCodes.length) return true

    const requestParams = {
      crowdCodes: missingCrowdCodes,
      [matchParamType]: identityValue,
    }
    const requestedCodeSet = new Set(missingCrowdCodes)
    const currentRequest = (async () => {
      const crowdResponse = await store.dispatch('queryUserCrowd', requestParams)
      if (currentSequence !== requestSequence) return false
      if (crowdResponse?.code !== 1 || !Array.isArray(crowdResponse.data)) {
        throw new Error('用户人群包响应异常')
      }

      const nextMatchedCrowdCodes = new Set(
        matchedCrowdCodesByType[matchParamType].value,
      )
      crowdResponse.data.forEach((item) => {
        if (
          item &&
          typeof item.crowdCode === 'string' &&
          requestedCodeSet.has(item.crowdCode) &&
          item.status === true
        ) {
          nextMatchedCrowdCodes.add(item.crowdCode)
        }
      })

      matchedCrowdCodesByType[matchParamType].value = nextMatchedCrowdCodes
      queriedCrowdCodesByType[matchParamType] = new Set([
        ...queriedCrowdCodesByType[matchParamType],
        ...missingCrowdCodes,
      ])
      return true
    })()

    activeCrowdRequestsByType[matchParamType] = currentRequest
    try {
      return await currentRequest
    } finally {
      if (activeCrowdRequestsByType[matchParamType] === currentRequest) {
        activeCrowdRequestsByType[matchParamType] = null
      }
    }
  }

  const initializeCrowdPackageAccess = async (dslContent = [], webPageType) => {
    const crowdCodesByType = collectCrowdCodesByType(dslContent, webPageType)
    const requiredMatchParamTypes = MATCH_PARAM_TYPES.filter(
      (matchParamType) => crowdCodesByType[matchParamType].size,
    )

    if (!requiredMatchParamTypes.length) {
      accessStatus.value = 'success'
      return
    }

    const token = getQueryString(route.query.token)
    if (!token) {
      resetAccessState('', 'error')
      return
    }
    if (activeToken !== token) resetAccessState(token)

    const currentSequence = requestSequence
    accessStatus.value = 'loading'

    try {
      const userInfo = await getUserInfo(token, currentSequence)
      if (!userInfo || currentSequence !== requestSequence) return

      const queryResults = await Promise.all(
        requiredMatchParamTypes.map((matchParamType) => {
          const identityValue = userInfo[matchParamType]
          if (typeof identityValue !== 'string' || !identityValue) return false
          return queryCrowdCodes(
            matchParamType,
            Array.from(crowdCodesByType[matchParamType]),
            identityValue,
            currentSequence,
          ).catch(() => false)
        }),
      )
      if (currentSequence !== requestSequence) return
      accessStatus.value = queryResults.every(Boolean) ? 'success' : 'error'
    } catch (error) {
      if (currentSequence !== requestSequence) return
      accessStatus.value = 'error'
    }
  }

  const hasCrowdPackageAccess = (
    packageCodes = [],
    matchParamType,
    webPageType,
  ) => {
    const resolvedMatchParamType = getMatchParamType(
      matchParamType,
      webPageType,
    )
    const normalizedPackageCodes = normalizeCrowdCodes(packageCodes)
    if (!normalizedPackageCodes.length) return false

    return normalizedPackageCodes.some((crowdCode) =>
      matchedCrowdCodesByType[resolvedMatchParamType].value.has(crowdCode),
    )
  }

  return {
    accessStatus,
    initializeCrowdPackageAccess,
    hasCrowdPackageAccess,
  }
}

export default useCrowdPackageAccess
